import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientsAPI } from '../services/api';
import { formatDateBritish } from '../utils/dateUtils';
import PostcodeLookupInput from '../components/PostcodeLookupInput';
import './ClientList.css';
import additionIcon from '../assets/icons/addition.png';
import editIcon from '../assets/icons/edit.png';
import deleteIcon from '../assets/icons/recycle-bin.png';
import detailIcon from '../assets/icons/detail.png';

interface Client {
  id: number;
  companyName: string;
  contactEmail: string;
  contactPhone: string;
  contactPerson: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  county?: string;
  postcode: string;
  policyStartDate?: string;
  policyEndDate?: string;
  vehicleCount?: number;
  driverCount?: number;
  isActive?: boolean;
  createdAt: string;
  createdBy?: string;
  notes?: string;
}

const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    contactEmail: '',
    contactPhone: '',
    contactPerson: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    policyStartDate: '',
    policyEndDate: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (searchParams.get('onboard') === 'true') setShowForm(true);
  }, [searchParams]);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      // Sort clients A-Z by company name
      const sortedClients = response.data.sort((a: Client, b: Client) => 
        a.companyName.localeCompare(b.companyName)
      );
      setClients(sortedClients);
      setFilteredClients(sortedClients);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.companyName.toLowerCase().includes(query.toLowerCase()) ||
        client.contactPerson.toLowerCase().includes(query.toLowerCase()) ||
        client.city.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingClient) {
        const updateData = {
          id: editingClient.id,
          ...formData,
          isActive: editingClient.isActive || true,
          createdAt: editingClient.createdAt,
          createdBy: editingClient.createdBy || 'System'
        };
        await clientsAPI.update(editingClient.id, updateData);
      } else {
        await clientsAPI.create(formData);
      }
      
      loadClients();
      resetForm();
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client');
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      companyName: client.companyName,
      contactEmail: client.contactEmail,
      contactPhone: client.contactPhone,
      contactPerson: client.contactPerson,
      addressLine1: client.addressLine1,
      addressLine2: client.addressLine2 || '',
      city: client.city,
      county: client.county || '',
      postcode: client.postcode,
      policyStartDate: client.policyStartDate ? new Date(client.policyStartDate).toISOString().split('T')[0] : '',
      policyEndDate: client.policyEndDate ? new Date(client.policyEndDate).toISOString().split('T')[0] : '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      await clientsAPI.delete(id);
      loadClients();
    } catch (error: any) {
      console.error('Error deleting client:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete client';
      alert(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      contactEmail: '',
      contactPhone: '',
      contactPerson: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postcode: '',
      policyStartDate: '',
      policyEndDate: '',
    });
    setEditingClient(null);
    setShowForm(false);
  };

  if (loading) {
    return <div>Loading clients...</div>;
  }

  return (
    <div className="client-list">
      <div className="page-header">
        <h1>Clients</h1>
        <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              padding: '10px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              width: '300px'
            }}
          />
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            <img src={additionIcon} alt="" style={{width: '16px', height: '16px', marginRight: '8px'}} />
            Add Client
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="close-btn" onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  placeholder="Company Name Ltd"
                  autoComplete="off"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Contact Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Contact Person *</label>
                <input
                  type="text"
                  required
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  placeholder="Primary contact name"
                />
              </div>

              {/* Postcode lookup — populates all address fields */}
              <div style={{ padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, marginBottom: 16 }}>
                <label style={{ fontWeight: 700, fontSize: 13, color: '#0c4a6e', display: 'block', marginBottom: 8 }}>🔍 Search by Postcode</label>
                <PostcodeLookupInput
                  onSelect={a => setFormData(prev => ({
                    ...prev,
                    addressLine1: a.addressLine1,
                    addressLine2: a.addressLine2,
                    city:         a.city,
                    county:       a.county,
                    postcode:     a.postcode,
                  }))}
                />
                <p style={{ margin: 0, fontSize: 12, color: '#0369a1' }}>Or enter the address manually below</p>
              </div>

              <div className="form-group">
                <label>Address Line 1 *</label>
                <input
                  type="text"
                  required
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  placeholder="Street address"
                />
              </div>

              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  placeholder="Apartment, suite, unit, etc. (optional)"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City or town"
                  />
                </div>

                <div className="form-group">
                  <label>County</label>
                  <input
                    type="text"
                    value={formData.county}
                    onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                    placeholder="County (optional)"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Postcode *</label>
                <input
                  type="text"
                  required
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="e.g. M1 1AA"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Policy Start Date</label>
                  <input
                    type="date"
                    value={formData.policyStartDate}
                    onChange={(e) => setFormData({ ...formData, policyStartDate: e.target.value })}
                  />
                  <small style={{ color: '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Start date of the client's policy period (can be adjusted)
                  </small>
                </div>

                <div className="form-group">
                  <label>Policy End Date</label>
                  <input
                    type="date"
                    value={formData.policyEndDate}
                    onChange={(e) => setFormData({ ...formData, policyEndDate: e.target.value })}
                  />
                  <small style={{ color: '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Fixed end date - serves as guardrail for vehicle cover dates
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Update' : 'Create'} Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        {filteredClients.length === 0 ? (
          <p className="empty-state">{searchQuery ? 'No clients match your search.' : 'No clients found. Add your first client to get started.'}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact Email</th>
                <th>Contact Phone</th>
                <th>Address</th>
                <th>Policy Period</th>
                <th>Vehicles</th>
                <th>Drivers</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id}>
                  <td><strong>{client.companyName}</strong></td>
                  <td>{client.contactEmail}</td>
                  <td>{client.contactPhone}</td>
                  <td>
                    {client.addressLine1}{client.addressLine2 ? `, ${client.addressLine2}` : ''}, {client.city}, {client.postcode}
                  </td>
                  <td>
                    {client.policyStartDate && client.policyEndDate ? (
                      <div style={{ fontSize: '13px' }}>
                        <div>{formatDateBritish(client.policyStartDate)} -</div>
                        <div>{formatDateBritish(client.policyEndDate)}</div>
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>
                    )}
                  </td>
                  <td>
                    <button 
                      className="btn-link"
                      onClick={() => navigate(`/vehicles?clientId=${client.id}`)}
                      style={{ cursor: 'pointer', color: '#003366', textDecoration: 'underline', border: 'none', background: 'none', padding: 0 }}
                    >
                      {client.vehicleCount || 0}
                    </button>
                  </td>
                  <td>
                    <button 
                      className="btn-link"
                      onClick={() => navigate(`/drivers?clientId=${client.id}`)}
                      style={{ cursor: 'pointer', color: '#003366', textDecoration: 'underline', border: 'none', background: 'none', padding: 0 }}
                    >
                      {client.driverCount || 0}
                    </button>
                  </td>
                  <td>{formatDateBritish(client.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" onClick={() => navigate(`/clients/${client.id}`)} title="View client details">
                        <img src={detailIcon} alt="Details" style={{width: '16px', height: '16px'}} />
                      </button>
                      {/* Protect Yourshield Ltd superuser client from edit/delete */}
                      {client.companyName?.toLowerCase().includes('yourshield') ? (
                        <span style={{ color: '#718096', fontSize: '12px', fontStyle: 'italic', marginLeft: '8px' }}>
                          Protected
                        </span>
                      ) : (
                        <>
                          <button className="btn-icon" onClick={() => handleEdit(client)} title="Edit client">
                            <img src={editIcon} alt="Edit" style={{width: '16px', height: '16px'}} />
                          </button>
                          <button className="btn-icon" onClick={() => handleDelete(client.id)} title="Delete client">
                            <img src={deleteIcon} alt="Delete" style={{width: '16px', height: '16px'}} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
};

export default ClientList;
